import { NgClass } from "@angular/common";
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  Output,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, RouterLink } from "@angular/router";
import { IconComponent } from "@claas/claas-ui-components";
import { ToggleComponent } from "@claas/claas-ui-components/form/toggle";
import { TranslatePipe } from "@ngx-translate/core";
import { AsideModalDialog } from "../../../shared/components/aside-modal-dialog/aside-modal-dialog";
import { ProductFamilyIconMap } from "../../../shared/models/product-family-icon-map";
import { ProductSeriesClusteredByFamily } from "../../../shared/models/product-series-clustered-by-family";
import * as productFamilyIconMapJson from "../../../shared/pia-api/fam-icon-map.json";
import { DefaultProduct } from "../../../shared/pia-api/generated";
import { ProductFamilyFavoritesService } from "../../../shared/services/product-family-favorites.service";
import { ProductFamilyFilterService } from "../../../shared/services/product-family-filter.service";
import { ShowOnlyFavoritesService } from "../../../shared/services/show-only-favorites.service";
import { WebStorage } from "../../../shared/services/web-storage";
import { ProductFamilyFilters } from "../product-family-filters/product-family-filters";

@Component({
  selector: "claas-product-family-aside",
  imports: [
    TranslatePipe,
    IconComponent,
    NgClass,
    RouterLink,
    ProductFamilyFilters,
    AsideModalDialog,
    ToggleComponent,
  ],
  templateUrl: "./product-family-aside.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./product-family-aside.scss",
})
export class ProductFamilyAside {
  private route = inject(ActivatedRoute);
  private productFamilyFilterService = inject(ProductFamilyFilterService);
  private productFavoriteFamilyService = inject(ProductFamilyFavoritesService);
  private showOnlyFavoritesService = inject(ShowOnlyFavoritesService);

  data = toSignal(this.route.data);
  excludedIds = toSignal(this.productFamilyFilterService.excludedIds$);
  favoriteIds = toSignal(this.productFavoriteFamilyService.includedIds$);
  onlyFavoritesStatus = toSignal(
    this.showOnlyFavoritesService.showOnlyFavoritesStatus$,
  );
  productFamiliesFiltered = computed(() => {
    const families = this.data()?.["productFamilies"] as DefaultProduct[];
    const excluded = this.excludedIds() ?? [];
    return (
      families?.filter(
        (productFamily: DefaultProduct) =>
          productFamily.ID &&
          !excluded.includes(productFamily.ID) &&
          (!this.onlyFavoritesStatus() ||
            (this.onlyFavoritesStatus() &&
              this.productSeries()?.[productFamily.ID]?.length)),
      ) ?? []
    );
  });

  productSeries = computed(() => {
    const productSeriesCurrent = this.data()?.["productSeries"];
    if (!productSeriesCurrent) {
      return productSeriesCurrent;
    }

    const productSeriesFilteredByFavorites =
      {} as ProductSeriesClusteredByFamily;

    for (const key in productSeriesCurrent) {
      if (this.onlyFavoritesStatus()) {
        // filter out all items that are not marked as favorites
        const favoriteProductSeries = productSeriesCurrent[key].filter(
          (productSerie: DefaultProduct) => {
            return this.favoriteIds()?.includes(productSerie.ID as string);
          },
        );

        if (favoriteProductSeries.length) {
          productSeriesFilteredByFavorites[key] = favoriteProductSeries;
        }
      } else {
        // leave the object as it is, favorites are ignored
        productSeriesFilteredByFavorites[key] = productSeriesCurrent[key];
      }
    }
    return productSeriesFilteredByFavorites;
  });

  productFamilyIconMap: ProductFamilyIconMap =
    productFamilyIconMapJson as ProductFamilyIconMap;
  filterOpenOnSmallerViewports = signal(false);

  isToggleFavouritesChecked = signal(false);

  toggleFilterButton =
    viewChild<ElementRef<HTMLButtonElement>>("toggleFilterButton");

  showAsideModal = false;

  storage = inject(WebStorage);

  @Output()
  showFavoritesOnly = new EventEmitter();

  isChecked = computed(() => {
    return this.onlyFavoritesStatus() as boolean;
  });

  setShowAsideModal(val: boolean) {
    this.showAsideModal = val;
  }

  handleToggleFavouritesChange(isChecked: boolean) {
    this.isToggleFavouritesChecked.set(isChecked);
    this.storage.favoriteProductsOnly = isChecked;
    this.showFavoritesOnly.emit(this.isToggleFavouritesChecked());
    this.showOnlyFavoritesService.setOnlyFavoritesStatus(isChecked);
  }

  handleFilterButtonDisplayClick(forceValue?: boolean) {
    const toggleState =
      typeof forceValue === "boolean"
        ? forceValue
        : !this.filterOpenOnSmallerViewports();

    this.filterOpenOnSmallerViewports.set(toggleState);

    if (!toggleState) {
      this.toggleFilterButton()?.nativeElement?.focus();
    }
  }
}
